'use client';

import { useQuery } from '@tanstack/react-query';
import type { SportSeason } from '@sharpr/types';

/**
 * Public season calendar (`sport_seasons`) for off-season UI gating on ANON-capable
 * surfaces — the pricing page and the sport switcher — where `useAccessState`'s
 * `sportSeasons` isn't available (it's auth-only). Backed by the edge-cached public
 * `/api/billing/seasons`. Returns `[]` until loaded or on error, so callers that gate on
 * `isWithinSeasonCtaWindow` (which fails OPEN on a missing season) degrade to "in-season"
 * — a data hiccup never wrongly blocks signup or navigation.
 */
export function useSportSeasons(): SportSeason[] {
  const { data } = useQuery({
    queryKey: ['sport-seasons'],
    queryFn: async (): Promise<SportSeason[]> => {
      const res = await fetch('/api/billing/seasons');
      if (!res.ok) return [];
      const json = await res.json();
      return (json?.data?.seasons ?? []) as SportSeason[];
    },
    staleTime: 5 * 60 * 1000, // matches the endpoint's billingCatalog s-maxage
    gcTime: 30 * 60 * 1000,
  });
  return data ?? [];
}
